import { runAllLimitingConcurrency } from 'base/concurrency';
import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import { useDocumentTitle } from 'base/ui/document_title';
import { Inline, Rows } from 'base/ui/layout';
import { DividerLine } from 'base/ui/panel';
import { TextRegular, TextRegularLight, Title5 } from 'base/ui/typography';
import { LLMProxyService } from 'blocks/llm_proxy_service';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { measureTextLines } from '../../component_renderer/text_measuring';
import { fitTextToNumLines } from './text_fitting';

export function installTextFittingPlayground(llm: LLMProxyService) {
  return function _TextFittingPlayground() {
    return <TextFittingPlayground llm={llm} />;
  };
}

type TextStyle = {
  fontSize: number;
  fontWeight: number;
  width: number;
};

class TestCase {
  readonly name: string;
  @observable.ref
  output:
    | Readonly<
        { loading: true } | { loading: false; text: string; apology?: string }
      >
    | undefined = undefined;

  constructor(
    readonly originalText: string,
    readonly style: TextStyle,
    readonly targetLineCount: number,
  ) {
    makeObservable(this);
    const lineCount = this.measure(originalText);
    this.name = `${lineCount} → ${targetLineCount} lines`;
  }

  async run(llm: LLMProxyService) {
    this.output = { loading: true };
    try {
      const { text, apology } = await fitTextToNumLines(
        this.originalText,
        this.measure,
        this.targetLineCount,
        llm,
        'en-US',
      );
      this.output = { loading: false, text, apology };
    } catch (e) {
      console.error('Error text fitting', e);
      this.output = undefined;
    }
  }

  @computed
  get error() {
    const { output } = this;
    if (!output) return 'Not started';
    if (output.loading) return 'Loading...';
    return output.apology;
  }

  private readonly measure = (text: string) => {
    const el = document.createElement('div');
    el.style.fontSize = `${this.style.fontSize}px`;
    el.style.fontWeight = `${this.style.fontWeight}`;
    el.style.width = `${this.style.width}px`;
    el.innerText = text;
    el.style.position = 'absolute';
    el.style.visibility = 'hidden';
    el.style.top = '0';
    el.style.left = '0';
    document.body.appendChild(el);
    try {
      return measureTextLines(el)?.lineCount;
    } finally {
      document.body.removeChild(el);
    }
  };
}

const HEADING_STYLE = {
  fontSize: 56,
  fontWeight: 700,
  width: 600,
};
const BODY_STYLE = {
  fontSize: 18,
  fontWeight: 400,
  width: 600,
};
const TEST_CASES = [
  {
    text: 'Empowering Urban Mobility',
    style: HEADING_STYLE,
    targetLines: 3,
  },
  {
    text: 'Empowering Urban Mobility',
    style: HEADING_STYLE,
    targetLines: 1,
  },
  {
    text: 'At WheelyShare, our mission is to revolutionize urban transportation by providing e-scooter sharing solutions for a greener, more efficient city.',
    style: BODY_STYLE,
    targetLines: 3,
  },
  {
    text: 'At WheelyShare, our mission is to revolutionize urban transportation by providing e-scooter sharing solutions for a greener, more efficient city.',
    style: BODY_STYLE,
    targetLines: 1,
  },
  {
    text: 'Discover the Impressive Growth of WheelyShare',
    style: HEADING_STYLE,
    targetLines: 3,
  },
  {
    text: 'Discover the Impressive Growth of WheelyShare',
    style: HEADING_STYLE,
    targetLines: 1,
  },
  {
    text: 'Since its launch, WheelyShare has revolutionized transportation in Sydney, providing a convenient and eco-friendly way to navigate the city. With a rapidly growing user base and an expanding fleet of e-scooters, WheelyShare continues to make a positive impact on urban mobility.',
    style: BODY_STYLE,
    targetLines: 5,
  },
  {
    text: 'Since its launch, WheelyShare has revolutionized transportation in Sydney, providing a convenient and eco-friendly way to navigate the city. With a rapidly growing user base and an expanding fleet of e-scooters, WheelyShare continues to make a positive impact on urban mobility.',
    style: BODY_STYLE,
    targetLines: 2,
  },
];

const TextFittingPlayground = observer(function _TFPlayground({
  llm,
}: {
  llm: LLMProxyService;
}) {
  useDocumentTitle('Text fitting playground');
  const testCases = React.useMemo(
    () => TEST_CASES.map((c) => new TestCase(c.text, c.style, c.targetLines)),
    [],
  );
  const runAll = () =>
    runAllLimitingConcurrency(
      testCases.map((c) => () => c.output ? Promise.resolve() : c.run(llm)),
    );
  return (
    <Rows spacing={16}>
      <Title5>Text fitting test cases</Title5>
      <Button onClick={runAll} label="Run all" />
      <ul>
        {testCases.map((testCase) => {
          return (
            <li key={keyOfObject(testCase)}>
              <Inline spacing={4}>
                <TextRegular color="grey">{testCase.name}:</TextRegular>
                <TextRegular color={testCase.error ? 'red' : 'inherit'}>
                  {testCase.error || 'Correct'}
                </TextRegular>
              </Inline>
            </li>
          );
        })}
      </ul>
      {testCases.map((testCase) => (
        <TestCasePreview key={keyOfObject(testCase)} testCase={testCase} />
      ))}
    </Rows>
  );
});

const TestCasePreview = observer(function _TestCasePreview({
  testCase,
}: {
  testCase: TestCase;
}) {
  const textStyle = {
    width: testCase.style.width + 2,
    fontSize: testCase.style.fontSize,
    fontWeight: testCase.style.fontWeight,
    border: '1px solid black',
    marginLeft: 8,
  };
  return (
    <Rows spacing={8}>
      <DividerLine />
      <div style={textStyle}>{testCase.originalText}</div>
      <TextRegularLight>
        Target lines: {testCase.targetLineCount}
      </TextRegularLight>
      {testCase.output?.loading && <TextRegular>Loading...</TextRegular>}
      {testCase.output && !testCase.output.loading && (
        <>
          <TextRegular color={testCase.error ? 'red' : 'inherit'}>
            LLM output:
          </TextRegular>
          <div style={{ ...textStyle, background: 'white' }}>
            {testCase.output.text}
          </div>
        </>
      )}
    </Rows>
  );
});
